import type { Action as ReduxAction } from 'redux';
import { BATCH, batchActions } from 'redux-batched-actions';
import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/filter';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/bufferCount';
import 'rxjs/add/operator/merge';
import uuid from 'uuid';
import { log } from '@atlassian/jira-common-util-logging';
import {
	type FetchError,
	type ValidationError,
	isClientFetchError,
	isValidationError,
} from '@atlassian/jira-fetch';
import cardSetService from '../../services/card-set';
import type { Action } from '../../state/actions';
import {
	type BulkOpsFailureReason,
	bulkOpsShowFailedFlag,
} from '../../state/actions/bulk-ops-flags/error';
import {
	type CardSetIndividualResponseFailureAction,
	type CardSetIndividualResponseSuccessAction,
	cardSetRequest,
	CARD_SET_INDIVIDUAL_REQUEST_SUCCESS,
	CARD_SET_INDIVIDUAL_REQUEST_FAILURE,
	CARD_SET_REQUEST,
	cardSetIndividualResponseSuccess,
	cardSetIndividualResponseFailure,
} from '../../state/actions/card-set-request';
import {
	type ActionWithCardSetMeta,
	CARD_SET_META_KEY,
} from '../../state/actions/meta/card-set-meta';
import type { ActionsObservable, State } from '../../state/types';

// Exported just for testing
export const sendRequestEpic = (action$: ActionsObservable) =>
	action$.ofType(CARD_SET_REQUEST).mergeMap((action) => {
		const { batchId, setMutations } = action.payload;

		return Observable.from(setMutations)
			.mergeMap((mutation) => {
				const {
					optimistic: { id: optimisticId },
					[CARD_SET_META_KEY]: { fieldId, issueKey, newValue, retryAction },
					analyticsEvent,
				} = mutation;

				return cardSetService({
					issueKey,
					fieldId,
					newValue,
				})
					.map(() =>
						cardSetIndividualResponseSuccess(
							{
								batchId,
								issueKey,
								fieldId,
							},
							optimisticId,
							analyticsEvent,
						),
					)
					.catch((error: FetchError | ValidationError | Error) => {
						log.safeErrorWithoutCustomerData(
							'bulk-ops-send-request-epic',
							`Failed to set card field '${fieldId}'`,
							error,
						);

						const isNetworkError = isClientFetchError(error);
						const isUserValidationError = isValidationError(error);

						// eslint-disable-next-line no-nested-ternary
						const failureReason = isNetworkError
							? 'network'
							: isUserValidationError
								? 'validation'
								: 'server';

						const statusCode = 'statusCode' in error ? error.statusCode : undefined;

						return Observable.of(
							cardSetIndividualResponseFailure(
								{
									batchId,
									issueKey,
									fieldId,
									retryAction,
									failureReason,
									statusCode,
								},
								optimisticId,
								analyticsEvent,
							),
						);
					});
			})
			.bufferCount(setMutations.length)
			.mergeMap((cardSetRequestResponses) => Observable.of(batchActions(cardSetRequestResponses)));
	});

const isActionWithCardSetMeta = (action: ReduxAction): action is ActionWithCardSetMeta =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	(action as ActionWithCardSetMeta).meta?.[CARD_SET_META_KEY] !== undefined;

const isCardSetIndividualResponse = (
	action: ReduxAction,
): action is CardSetIndividualResponseFailureAction | CardSetIndividualResponseSuccessAction =>
	action.type === CARD_SET_INDIVIDUAL_REQUEST_SUCCESS ||
	action.type === CARD_SET_INDIVIDUAL_REQUEST_FAILURE;

const isCardSetIndividualRequestFailure = (
	action: CardSetIndividualResponseFailureAction | CardSetIndividualResponseSuccessAction,
): action is CardSetIndividualResponseFailureAction =>
	action.type === CARD_SET_INDIVIDUAL_REQUEST_FAILURE;

const getFailureReason = (
	failedActions: CardSetIndividualResponseFailureAction[],
): BulkOpsFailureReason => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const firstFailureReason = failedActions[0].payload.failureReason!;
	const isAllSameFailureReason = failedActions.every(
		(action) => action.payload.failureReason === firstFailureReason,
	);

	return isAllSameFailureReason ? firstFailureReason : 'mixed';
};

// Exported just for testing
export const bulkOpsEpic = (action$: ActionsObservable) =>
	action$.ofType(BATCH).mergeMap((action) => {
		const cardSetMetaActions = action.payload.filter(isActionWithCardSetMeta);

		if (cardSetMetaActions.length < 1) {
			return Observable.empty<never>();
		}
		const cardSetActions$ = Observable.from(cardSetMetaActions).map(
			(actionWithCardSetMeta) => actionWithCardSetMeta.meta,
		);

		const batchId = uuid.v4();

		const request$ = cardSetActions$
			.bufferCount(cardSetMetaActions.length)
			.map((setMutations) => cardSetRequest(setMutations, batchId));

		const response$ = action$
			.ofType(BATCH)
			.mergeMap((batchAction) => batchAction.payload)
			.filter(isCardSetIndividualResponse)
			.filter((cardSetIndividualResponse) => cardSetIndividualResponse.payload.batchId === batchId)
			.bufferCount(cardSetMetaActions.length)
			.mergeMap((cardSetRequestResponses) => {
				const failedActions = cardSetRequestResponses.filter(isCardSetIndividualRequestFailure);

				if (!failedActions.length) {
					return Observable.empty<never>();
				}

				const totalCount = cardSetRequestResponses.length;

				const previousFailedBatchIds = cardSetMetaActions[0].meta.failedBatchIds;

				const failedActionBuilders = failedActions.map(
					(failedAction) => failedAction.payload.retryAction,
				);
				const failedIssueKeys = failedActions.map((failedAction) => failedAction.payload.issueKey);

				const failedBatchIds = previousFailedBatchIds?.length
					? previousFailedBatchIds.concat(batchId)
					: [batchId];

				return Observable.of(
					bulkOpsShowFailedFlag({
						failedActionBuilders,
						failedIssueKeys,
						totalCount,
						failedBatchIds,
						failureReason: getFailureReason(failedActions),
					}),
				);
			});

		return request$.merge(response$);
	});

export default combineEpics<Action, State>(bulkOpsEpic, sendRequestEpic);
